<div class="provision-form-container">
  <!-- Loading Spinner -->
  @if (saving || loadingProvisionData) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>{{ saving ? 'Saving provision...' : 'Loading provision data...' }}</p>
    </div>
  }

  <div class="form-content">

    <!-- Header Section -->
    <div class="header-section">
      <div class="title-section">
        <h1>{{ isEditMode ? 'Edit Approved Provision' : 'New Approved Provision' }}</h1>
        <p class="subtitle">{{ isEditMode ? 'Update approved provision details' : 'Create a new approved provision record' }}</p>
        <p class="required-fields-note">Fields marked with <span class="required-indicator">*</span> are required</p>
      </div>

      <div class="header-actions">
        <button mat-stroked-button color="accent" (click)="cancelSubmit()">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary"
                [disabled]="!isFormValid() || saving"
                (click)="onSubmit()">
          <mat-icon>save</mat-icon>
          {{ isEditMode ? 'Update Provision' : 'Save Provision' }}
        </button>
      </div>
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <!-- Basic Information Card -->
      <mat-card class="hero-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>description</mat-icon>
            Provision Information
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid full-width">
            <mat-form-field appearance="outline">
              <mat-label>Provision Name <span class="required-indicator">*</span></mat-label>
              <mat-select formControlName="provName" required>
                @for (name of provisionNames; track name) {
                  <mat-option [value]="name">{{ name }}</mat-option>
                }
              </mat-select>
              @if (!form.get('provName')?.value) {
                <mat-error>Provision name is required</mat-error>
              }
            </mat-form-field>
          </div>

          <div class="form-grid two-column">
            <mat-form-field appearance="outline">
              <mat-label>Contract Type</mat-label>
              <mat-select formControlName="contractTypes" multiple>
                @for (type of contractTypes; track type) {
                  <mat-option [value]="type">{{ type }}</mat-option>
                }
              </mat-select>
              <mat-hint>Select one or more contract types</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Risk Category</mat-label>
              <mat-select formControlName="riskCategory">
                @for (category of riskCategories; track category) {
                  <mat-option [value]="category">{{ category }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div class="form-grid full-width">
            <div class="editor-field">
              <label class="editor-label">Provision Language / Description</label>
              <p-editor formControlName="description"
                        [style]="{'height':'200px'}"
                        placeholder="Enter the provision language or description">
              </p-editor>
            </div>
          </div>

          <!-- Key Phrases Section -->
          <div class="form-grid full-width key-phrases-section">
            <label class="section-label">Key Phrases</label>
            <p class="field-hint">Type a word or phrase and press Enter to add it. These phrases will be used for AI analysis.</p>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Add key phrases</mat-label>
              <mat-chip-grid #chipGrid>
                @for (phrase of keyPhrases; track phrase) {
                  <mat-chip-row (removed)="removeKeyPhrase(phrase)">
                    {{ phrase }}
                    <button matChipRemove>
                      <mat-icon>cancel</mat-icon>
                    </button>
                  </mat-chip-row>
                }
              </mat-chip-grid>
              <input matInput
                     [(ngModel)]="keyPhraseInput"
                     [ngModelOptions]="{standalone: true}"
                     [matChipInputFor]="chipGrid"
                     (matChipInputTokenEnd)="addKeyPhrase($event)"
                     (keydown)="onKeyPhraseInputKeydown($event)"
                     placeholder="Type and press Enter...">
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Owner Card -->
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>person</mat-icon>
            Owner
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid full-width">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Owner</mat-label>
              <input matInput
                     type="text"
                     formControlName="owner"
                     [matAutocomplete]="ownerAuto"
                     (input)="filterUsers($event)"
                     placeholder="Search for owner">
              <mat-icon matSuffix>person</mat-icon>
              <mat-autocomplete #ownerAuto="matAutocomplete" [displayWith]="displayUserFn">
                @for (user of filteredUsers; track user.userNum) {
                  <mat-option [value]="user">{{ user.friendlyName }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Approver Card -->
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>verified_user</mat-icon>
            Approver
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid full-width">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Approver</mat-label>
              <input matInput
                     type="text"
                     formControlName="approver"
                     [matAutocomplete]="approverAuto"
                     (input)="filterUsers($event)"
                     placeholder="Search for approver">
              <mat-icon matSuffix>verified_user</mat-icon>
              <mat-autocomplete #approverAuto="matAutocomplete" [displayWith]="displayUserFn">
                @for (user of filteredUsers; track user.userNum) {
                  <mat-option [value]="user">{{ user.friendlyName }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          </div>

          <div class="form-grid full-width">
            <mat-form-field appearance="outline" class="full-width required-action-field">
              <mat-label>Required Action</mat-label>
              <textarea matInput
                        formControlName="requiredAction"
                        rows="4"
                        placeholder="Enter required action for approval"></textarea>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Dates and Actions Card -->
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>event</mat-icon>
            Dates & Actions
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid two-column">
            <mat-form-field appearance="outline">
              <mat-label>Critical Date</mat-label>
              <input matInput
                     [matDatepicker]="criticalDatePicker"
                     formControlName="criticalDate"
                     placeholder="Select critical date">
              <mat-datepicker-toggle matSuffix [for]="criticalDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #criticalDatePicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Action Date</mat-label>
              <input matInput
                     [matDatepicker]="actionDatePicker"
                     formControlName="actionDate"
                     placeholder="Select action date">
              <mat-datepicker-toggle matSuffix [for]="actionDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #actionDatePicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div class="form-grid two-column action-summary-row">
            <mat-form-field appearance="outline">
              <mat-label>Action Summary</mat-label>
              <mat-select formControlName="actionSummary">
                @for (summary of actionSummaries; track summary) {
                  <mat-option [value]="summary">{{ summary }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <div class="action-completed-container">
              <mat-checkbox formControlName="actionCompleted" class="action-checkbox">
                Action Completed
              </mat-checkbox>
              @if (form.get('approvedProvision')?.value && !form.get('actionCompleted')?.value) {
                <p class="playbook-hint">
                  <mat-icon>info</mat-icon>
                  Mark <strong>Action Completed</strong> to publish this approved provision to the Playbook.
                </p>
              }
            </div>
          </div>

          <!-- Comments/Recommendations -->
          <div class="form-grid full-width comments-section">
            <div class="editor-field">
              <label class="editor-label">Comments / Recommendations</label>
              <p-editor formControlName="commentsRecommendations"
                        [style]="{'height':'150px'}"
                        placeholder="Enter any comments or recommendations">
              </p-editor>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Source Information (Collapsible) -->
      <mat-expansion-panel [expanded]="!!form.get('sourceType')?.value">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>link</mat-icon>
            Source Information
          </mat-panel-title>
        </mat-expansion-panel-header>
        <div class="expansion-content">
          <div class="form-grid two-column">
            <mat-form-field appearance="outline">
              <mat-label>Source Type</mat-label>
              <input matInput formControlName="sourceType" readonly>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Source Name</mat-label>
              <input matInput formControlName="contractFriendlyName" readonly>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Source ID</mat-label>
              <input matInput formControlName="sourceId" readonly>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Contract Number</mat-label>
              <input matInput formControlName="contractNum" readonly>
            </mat-form-field>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Approved Provision toggle (controls risk-scoring vs approved mode + Playbook eligibility) -->
      <mat-card class="info-card">
        <mat-card-content>
          <mat-checkbox formControlName="approvedProvision"
                        class="action-checkbox"
                        (change)="showRiskScoring = !$event.checked">
            Approved Provision
          </mat-checkbox>
          <p class="approved-toggle-hint">
            When checked, risk scoring is skipped and this becomes an approved provision. Combined with
            <strong>Action Completed</strong>, it publishes to the Playbook for its selected contract type(s).
          </p>
        </mat-card-content>
      </mat-card>

      <!-- Risk Scoring Section -->
      @if (showRiskScoring) {
        <mat-card class="info-card risk-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon>assessment</mat-icon>
              Risk Scoring
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="form-grid two-column">
              <mat-form-field appearance="outline">
                <mat-label>Risk Domain</mat-label>
                <mat-select formControlName="riskDomain">
                  @for (domain of riskDomains; track domain) {
                    <mat-option [value]="domain">{{ domain }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Risk Name</mat-label>
                <input matInput
                       formControlName="riskName"
                       placeholder="Enter risk name">
              </mat-form-field>
            </div>

            <div class="risk-scores-grid">
              <div class="score-section">
                <label class="score-label">Likelihood Score: {{ form.get('likelihoodScore')?.value || 0 }}</label>
                <p class="score-description">{{ getRiskScoreLabel(form.get('likelihoodScore')?.value) }}</p>
                <mat-slider min="0" max="5" step="1" discrete [displayWith]="formatSliderLabel">
                  <input matSliderThumb formControlName="likelihoodScore">
                </mat-slider>
              </div>

              <div class="score-section">
                <label class="score-label">Impact Score: {{ form.get('impactScore')?.value || 0 }}</label>
                <p class="score-description">{{ getRiskScoreLabel(form.get('impactScore')?.value) }}</p>
                <mat-slider min="0" max="5" step="1" discrete [displayWith]="formatSliderLabel">
                  <input matSliderThumb formControlName="impactScore">
                </mat-slider>
              </div>
            </div>

            <div class="risk-scale-image">
              <img src="assets/img/guides/risk_scale.jpg" alt="Risk Scale Guide" class="risk-guide">
            </div>
          </mat-card-content>
        </mat-card>
      }

      @if (!showRiskScoring) {
        <mat-card class="info-card approved-card">
          <mat-card-content>
            <div class="approved-provision-notice">
              <mat-icon>verified</mat-icon>
              <h2>Approved Provision</h2>
              <p>This is an approved provision - risk scoring is not required.</p>
            </div>
          </mat-card-content>
        </mat-card>
      }

      <!-- Bottom Action Bar -->
      <div class="bottom-actions">
        <button mat-stroked-button color="accent" (click)="cancelSubmit()" type="button">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary"
                [disabled]="!isFormValid() || saving"
                type="submit">
          <mat-icon>save</mat-icon>
          {{ isEditMode ? 'Update Provision' : 'Save Provision' }}
        </button>
      </div>

    </form>
  </div>
</div>
